import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * The variant's quantity limits (custom.min_qty / custom.max_qty, quantity rules, stock) come in
 * data-min-qty / data-max-qty, see snippets/variant-quantity-limits.liquid. The +/- buttons stop
 * at them. On a cart line, a typed value is pulled back within them. On the product page it is
 * left as typed: the product form adjusts it on add to cart and says why (ticket 1888).
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (this.#isAtLimit(event.target)) return;
    const { max } = this.limits;
    const next = this.#value + this.#step;
    this.refs.quantityInput.value = String(max === null ? next : Math.min(next, max));
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (this.#isAtLimit(event.target)) return;
    this.refs.quantityInput.value = String(Math.max(this.#value - this.#step, this.limits.floor));
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Copies the limits of another variant's input (rendered by the server on variant change) onto
   * this one. The current quantity is kept, only raised to the new variant's minimum.
   * @param {HTMLInputElement} source - The quantity input rendered for the new variant.
   */
  updateLimits(source) {
    const { quantityInput } = this.refs;

    for (const name of ['min', 'step', 'data-min-qty', 'data-max-qty']) {
      const value = source.getAttribute(name);
      if (value === null) quantityInput.removeAttribute(name);
      else quantityInput.setAttribute(name, value);
    }
    quantityInput.disabled = source.disabled;

    if (Number.isNaN(parseInt(quantityInput.value)) || this.#value < this.limits.floor) {
      quantityInput.value = String(this.limits.floor);
    }
    this.updateButtonStates();
  }

  /**
   * Marks - unavailable at the minimum and + at the maximum. That uses aria-disabled, not
   * disabled, so a focused button keeps focus when it reaches its limit (WCAG 2.4.3); only a
   * quantity that can't change at all disables them for real.
   */
  updateButtonStates() {
    const { quantityInput } = this.refs;
    const minus = this.querySelector('button[name="minus"]');
    const plus = this.querySelector('button[name="plus"]');
    if (!(minus instanceof HTMLButtonElement) || !(plus instanceof HTMLButtonElement)) return;

    // The server renders the buttons disabled when the line can't be updated at all.
    if (this.hasAttribute('data-locked')) return;

    const { floor, max } = this.limits;
    const locked = quantityInput.disabled;

    /** @type {[HTMLButtonElement, boolean][]} */
    const states = [
      [minus, this.#value <= floor],
      [plus, max !== null && this.#value >= max],
    ];
    for (const [button, atLimit] of states) {
      button.disabled = locked;
      if (!locked && atLimit) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }
  }

  /**
   * @param {HTMLElement} target - The clicked element, or one inside the button.
   * @returns {boolean} Whether the button is at its limit.
   */
  #isAtLimit(target) {
    return target.closest('button')?.getAttribute('aria-disabled') === 'true';
  }

  /**
   * The quantity limits of the input.
   * floor: the lowest value - can reach; max: the highest value + can reach (null: no limit).
   * @returns {{ min: number, floor: number, max: number | null }}
   */
  get limits() {
    const { quantityInput } = this.refs;
    const min = Number(quantityInput.min) || 0;
    const minQty = Number(quantityInput.dataset.minQty) || 0;
    const { maxQty } = quantityInput.dataset;

    return {
      min,
      floor: Math.max(min, minQty),
      max: maxQty === undefined || maxQty === '' ? null : Number(maxQty),
    };
  }

  get #value() {
    return parseInt(this.refs.quantityInput.value) || 0;
  }

  get #step() {
    return Number(this.refs.quantityInput.step) || 1;
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.updateButtonStates();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max } = this.limits;
    const value = parseInt(quantityInput.value);

    if (Number.isNaN(value)) {
      quantityInput.value = quantityInput.defaultValue || String(min || 1);
      return;
    }
    if (value < min) quantityInput.value = String(min);

    // Cart lines only: 0 still removes the line.
    if (quantityInput.dataset.cartLine === undefined) return;

    const minQty = Number(quantityInput.dataset.minQty) || 0;
    if (max !== null && value > max) quantityInput.value = String(max);
    if (value > 0 && value < minQty) quantityInput.value = String(minQty);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
